import { useLabelVersion } from '../../../../features/labels/LabelsProvider';
import { labelId } from '../../../../features/labels/labels';
import React, { useEffect, useMemo, useRef, useState } from "react";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import "./styles/CalendarioAnalitica.css";

const STORAGE_KEY = "RangoDiasScapp";
const QUICK_RANGE_INPUT_DEFAULT = "";

const normalizeRange = (start, end) => {
  if (!start || !end) return { start, end };
  return start <= end ? { start, end } : { start: end, end: start };
};

const formatStorageDate = (date) =>
  `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;

const formatNoteDayKey = (date) =>
  [
    String(date.getDate()).padStart(2, "0"),
    String(date.getMonth() + 1).padStart(2, "0"),
    date.getFullYear(),
  ].join("/");

const formatDisplayDate = (date, locale = undefined) =>
  date.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const parseDateString = (value) => {
  if (!value || typeof value !== "string") return null;
  const [day, month, year] = value.split("-").map(Number);
  if (!day || !month || !year) return null;
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

function CustomCalendar({
  dias,
  setDias,
  translate,
  notesByDay = {},
  showNoteSource = false,
}) {
  useLabelVersion();
  const t = (key, fallback, params) => {
    const value = typeof translate === "function" ? translate(key, params) : key;
    return value && value !== key ? value : fallback;
  };

  const today = useMemo(() => new Date(), []);
  const monthPickerRef = useRef(null);
  const yearPickerRef = useRef(null);

  const [displayedMonth, setDisplayedMonth] = useState(today.getMonth());
  const [displayedYear, setDisplayedYear] = useState(today.getFullYear());
  const [selectedRange, setSelectedRange] = useState({ start: today, end: today });
  const [isSelecting, setIsSelecting] = useState(false);
  const [activeQuickRange, setActiveQuickRange] = useState(null);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [daysInput, setDaysInput] = useState(QUICK_RANGE_INPUT_DEFAULT);

  const months = [
    t("month_short_jan", "Jan"),
    t("month_short_feb", "Feb"),
    t("month_short_mar", "Mar"),
    t("month_short_apr", "Apr"),
    t("month_short_may", "May"),
    t("month_short_jun", "Jun"),
    t("month_short_jul", "Jul"),
    t("month_short_aug", "Aug"),
    t("month_short_sep", "Sep"),
    t("month_short_oct", "Oct"),
    t("month_short_nov", "Nov"),
    t("month_short_dec", "Dec"),
  ];

  const weekdays = [
    t("weekday_mon", "Mon"),
    t("weekday_tue", "Tue"),
    t("weekday_wed", "Wed"),
    t("weekday_thu", "Thu"),
    t("weekday_fri", "Fri"),
    t("weekday_sat", "Sat"),
    t("weekday_sun", "Sun"),
  ];

  const years = useMemo(
    () => Array.from({ length: new Date().getFullYear() - 2020 + 2 }, (_, i) => 2020 + i),
    []
  );

  const syncRange = (startValue, endValue, quickKey = null) => {
    const { start, end } = normalizeRange(startValue, endValue);
    const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const payload = {
      startDate: formatStorageDate(start),
      endDate: formatStorageDate(end),
      dayRange: diffDays,
    };

    setSelectedRange({ start, end });
    setActiveQuickRange(quickKey);
    setDisplayedMonth(end.getMonth());
    setDisplayedYear(end.getFullYear());
    setDias(payload);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  };

  useEffect(() => {
    const parsedStart = parseDateString(dias?.startDate);
    const parsedEnd = parseDateString(dias?.endDate);

    if (parsedStart && parsedEnd) {
      const { start, end } = normalizeRange(parsedStart, parsedEnd);
      setSelectedRange({ start, end });
      setDisplayedMonth(end.getMonth());
      setDisplayedYear(end.getFullYear());
      return;
    }

    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
      const storageStart = parseDateString(stored?.startDate);
      const storageEnd = parseDateString(stored?.endDate);
      if (storageStart && storageEnd) {
        const { start, end } = normalizeRange(storageStart, storageEnd);
        setSelectedRange({ start, end });
        setDisplayedMonth(end.getMonth());
        setDisplayedYear(end.getFullYear());
      }
    } catch {
      // Ignore malformed persisted state.
    }
  }, [dias?.endDate, dias?.startDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMonthPicker && monthPickerRef.current && !monthPickerRef.current.contains(event.target)) {
        setShowMonthPicker(false);
      }
      if (showYearPicker && yearPickerRef.current && !yearPickerRef.current.contains(event.target)) {
        setShowYearPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMonthPicker, showYearPicker]);

  const getCalendarDays = (month, year) => {
    const first = new Date(year, month, 1);
    const startIdx = first.getDay() === 0 ? 6 : first.getDay() - 1;
    const last = new Date(year, month + 1, 0);
    const days = [];
    const prevLast = new Date(year, month, 0).getDate();
    let nextMonthDay = 1;

    for (let i = startIdx; i > 0; i -= 1) {
      days.push({
        date: new Date(year, month - 1, prevLast - i + 1),
        currentMonth: false,
      });
    }

    for (let day = 1; day <= last.getDate(); day += 1) {
      days.push({ date: new Date(year, month, day), currentMonth: true });
    }

    while (days.length % 7) {
      days.push({
        date: new Date(year, month + 1, nextMonthDay),
        currentMonth: false,
      });
      nextMonthDay += 1;
    }

    // Fijamos siempre 6 semanas para que el alto del calendario no cambie
    // entre meses cortos/largos y ambas tarjetas mantengan el mismo tamaño.
    while (days.length < 42) {
      days.push({
        date: new Date(year, month + 1, nextMonthDay),
        currentMonth: false,
      });
      nextMonthDay += 1;
    }

    return Array.from({ length: days.length / 7 }, (_, index) =>
      days.slice(index * 7, index * 7 + 7)
    );
  };

  const handleQuickRange = (key, start, end) => {
    setDaysInput(QUICK_RANGE_INPUT_DEFAULT);
    setIsSelecting(false);
    syncRange(start, end, key);
  };

  const handleSelectToday = () => handleQuickRange("today", today, today);

  const handleSelectYesterday = () => {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    handleQuickRange("yesterday", yesterday, yesterday);
  };

  const handleSelectThisWeek = () => {
    const dayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1;
    const start = new Date(today);
    start.setDate(today.getDate() - dayIndex);
    handleQuickRange("this_week", start, today);
  };

  const handleSelectLastWeek = () => {
    const dayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1;
    const startOfThisWeek = new Date(today);
    startOfThisWeek.setDate(today.getDate() - dayIndex);
    const start = new Date(startOfThisWeek);
    start.setDate(startOfThisWeek.getDate() - 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    handleQuickRange("last_week", start, end);
  };

  const handleSelectThisMonth = () => {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    handleQuickRange("this_month", start, today);
  };

  const handleSelectLastMonth = () => {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    handleQuickRange("last_month", start, end);
  };

  const handleDaysUntilToday = () => {
    const amount = Number(daysInput);
    if (!Number.isFinite(amount) || amount <= 0) return;
    const start = new Date(today);
    start.setDate(today.getDate() - amount + 1);
    handleQuickRange("days_until_today", start, today);
  };

  const handleDayClick = (day) => {
    setActiveQuickRange(null);

    if (!selectedRange.start || !isSelecting) {
      setSelectedRange({ start: day, end: day });
      setIsSelecting(true);
      return;
    }

    setIsSelecting(false);
    syncRange(selectedRange.start, day, null);
  };

  const handleMouseEnter = (day) => {
    if (isSelecting && selectedRange.start) {
      setSelectedRange((prev) => ({ ...prev, end: day }));
    }
  };

  const isInSelectedRange = (day) => {
    if (!selectedRange.start || !selectedRange.end) return false;
    const { start, end } = normalizeRange(selectedRange.start, selectedRange.end);
    const time = day.getTime();
    return time >= start.getTime() && time <= end.getTime();
  };

  const isRangeEdge = (day, edge) => {
    const target = selectedRange[edge];
    return target && day.toDateString() === target.toDateString();
  };

  const navigateMonth = (direction) => {
    let nextMonth = displayedMonth + direction;
    let nextYear = displayedYear;

    if (nextMonth < 0) {
      nextMonth = 11;
      nextYear -= 1;
    }

    if (nextMonth > 11) {
      nextMonth = 0;
      nextYear += 1;
    }

    setDisplayedMonth(nextMonth);
    setDisplayedYear(nextYear);
  };

  const rangeSummary = useMemo(() => {
    const { start, end } = normalizeRange(selectedRange.start, selectedRange.end);
    if (!start || !end) return "";

    const sameDay = start.toDateString() === end.toDateString();
    return sameDay
      ? formatDisplayDate(start)
      : `${formatDisplayDate(start)} - ${formatDisplayDate(end)}`;
  }, [selectedRange.end, selectedRange.start]);

  const rangeDays = useMemo(() => {
    const { start, end } = normalizeRange(selectedRange.start, selectedRange.end);
    if (!start || !end) return 0;
    return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
  }, [selectedRange.end, selectedRange.start]);

  const renderCalendar = (month, year, label) => {
    const weeks = getCalendarDays(month, year);

    return (
      <section className="calendar-card">
        <div className="calendar-card-header">
          <span className="calendar-card-label">{label}</span>
          <span className="calendar-card-title">
            {months[month]} {year}
          </span>
        </div>

        <table className="calendar-table">
          <thead>
            <tr>
              {weekdays.map((weekday) => (
                <th key={weekday}>{weekday}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, weekIndex) => (
              <tr key={`${month}-${year}-${weekIndex}`}>
                {week.map((cell) => {
                  const day = cell.date;
                  const isToday = day.toDateString() === today.toDateString();
                  const isSelected = isInSelectedRange(day);
                  const isStart = isRangeEdge(day, "start");
                  const isEnd = isRangeEdge(day, "end");
                  const dayNotes = notesByDay[formatNoteDayKey(day)] || [];
                  const notesTooltip = dayNotes.length
                    ? `${t(
                      "notes_calendar_count",
                      `${dayNotes.length} nota(s)`,
                      { count: dayNotes.length },
                    )}\n${dayNotes.map((note) => {
                      const centerName = showNoteSource
                        ? labelId(note.centro_id, 'rac', String(note.centro_id || "").split("-").filter(Boolean).pop())
                        : "";
                      return centerName ? `${centerName}: ${note.text}` : note.text;
                    }).join("\n")}`
                    : undefined;

                  return (
                    <td key={`${month}-${year}-${day.toISOString()}`}>
                      <button
                        type="button"
                        title={notesTooltip}
                        className={[
                          "calendar-day",
                          cell.currentMonth ? "" : "not-current",
                          isSelected ? "selected" : "",
                          isStart ? "range-start" : "",
                          isEnd ? "range-end" : "",
                          isToday ? "today" : "",
                          dayNotes.length ? "has-notes" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => handleDayClick(day)}
                        onMouseEnter={() => handleMouseEnter(day)}
                      >
                        <span>{day.getDate()}</span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    );
  };

  const previousMonth = displayedMonth === 0 ? 11 : displayedMonth - 1;
  const previousYear = displayedMonth === 0 ? displayedYear - 1 : displayedYear;

  return (
    <div className="calendar-component">
      <div className="calendar-toolbar">
        <div className="calendar-toolbar-copy">
          <span className="calendar-toolbar-kicker">{t("temporal_range", "Time range")}</span>
          <h3>{t("calendar", "Calendar")}</h3>
          <p>{rangeSummary}</p>
        </div>

        <div className="calendar-range-summary">
          <span>{t("days", "Days")}</span>
          <strong>{rangeDays}</strong>
        </div>
      </div>

      <div className="calendar-quick-actions">
        <button
          type="button"
          className={activeQuickRange === "today" ? "active" : ""}
          onClick={handleSelectToday}
        >
          {t("today", "Today")}
        </button>
        <button
          type="button"
          className={activeQuickRange === "yesterday" ? "active" : ""}
          onClick={handleSelectYesterday}
        >
          {t("yesterday", "Yesterday")}
        </button>
        <button
          type="button"
          className={activeQuickRange === "this_week" ? "active" : ""}
          onClick={handleSelectThisWeek}
        >
          {t("this_week", "This week")}
        </button>
        <button
          type="button"
          className={activeQuickRange === "last_week" ? "active" : ""}
          onClick={handleSelectLastWeek}
        >
          {t("last_week", "Last week")}
        </button>
        <button
          type="button"
          className={activeQuickRange === "this_month" ? "active" : ""}
          onClick={handleSelectThisMonth}
        >
          {t("this_month", "This month")}
        </button>
        <button
          type="button"
          className={activeQuickRange === "last_month" ? "active" : ""}
          onClick={handleSelectLastMonth}
        >
          {t("last_month", "Last month")}
        </button>

        <div className="calendar-days-input">
          <span>{t("days_until_today", "Days until today")}</span>
          <div className="calendar-days-input-group">
            <input
              type="number"
              min="1"
              value={daysInput}
              onChange={(event) => setDaysInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleDaysUntilToday();
                }
              }}
              placeholder="30"
            />
            <button type="button" onClick={handleDaysUntilToday}>
              {t("accept_button", "Apply")}
            </button>
          </div>
        </div>
      </div>

      <div className="calendar-navigation">
        <button type="button" className="calendar-nav-button" onClick={() => navigateMonth(-1)}>
          <KeyboardArrowLeftIcon />
        </button>

        <div className="calendar-navigation-center">
          <div className="calendar-picker" ref={monthPickerRef}>
            <button
              type="button"
              className="calendar-picker-trigger"
              onClick={() => {
                setShowMonthPicker((prev) => !prev);
                setShowYearPicker(false);
              }}
            >
              {months[displayedMonth]}
            </button>
            {showMonthPicker && (
              <div className="calendar-picker-menu">
                {months.map((monthLabel, index) => (
                  <button
                    type="button"
                    key={monthLabel}
                    className={index === displayedMonth ? "active" : ""}
                    onClick={() => {
                      setDisplayedMonth(index);
                      setShowMonthPicker(false);
                    }}
                  >
                    {monthLabel}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="calendar-picker" ref={yearPickerRef}>
            <button
              type="button"
              className="calendar-picker-trigger"
              onClick={() => {
                setShowYearPicker((prev) => !prev);
                setShowMonthPicker(false);
              }}
            >
              {displayedYear}
            </button>
            {showYearPicker && (
              <div className="calendar-picker-menu calendar-picker-menu-years">
                {years.map((year) => (
                  <button
                    type="button"
                    key={year}
                    className={year === displayedYear ? "active" : ""}
                    onClick={() => {
                      setDisplayedYear(year);
                      setShowYearPicker(false);
                    }}
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button type="button" className="calendar-nav-button" onClick={() => navigateMonth(1)}>
          <KeyboardArrowRightIcon />
        </button>
      </div>

      <div className="calendars-container">
        {renderCalendar(previousMonth, previousYear, t("previous", "Previous"))}
        {renderCalendar(displayedMonth, displayedYear, t("current", "Current"))}
      </div>
    </div>
  );
}

const CalendarioAnalitica = ({
  dias,
  setDias,
  translate,
  notesByDay,
  showNoteSource,
}) => (
  <div className="myAppContainer">
    <CustomCalendar
      dias={dias}
      setDias={setDias}
      translate={translate}
      notesByDay={notesByDay}
      showNoteSource={showNoteSource}
    />
  </div>
);

export default CalendarioAnalitica;
